﻿import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import authService from "@/services/authService";
import {
  KeyIcon,
  CheckCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

/**
 * PasskeyPromptBanner
 *
 * Shown once after the user's first successful login â€” only when the device
 * has a verified platform authenticator (fingerprint / Face ID / Windows Hello).
 *
 * Desktop PCs without biometric hardware will never see this banner because
 * `isUserVerifyingPlatformAuthenticatorAvailable()` returns false for them.
 *
 * Non-blocking: user can dismiss permanently.
 */
const STORAGE_KEY = "passkey_prompt_dismissed";

const PasskeyPromptBanner: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only show on authenticated app pages (dashboard, specialist, group-admin)
  const isAppPage = /\/(dashboard|specialist|group-admin|admin)(\/?|\/.*)$/.test(location.pathname);

  useEffect(() => {
    if (!user || !isAppPage) return;
    if (localStorage.getItem(STORAGE_KEY)) return;

    // Only show when the device has a real biometric / PIN platform authenticator.
    // This returns false on desktop PCs without Windows Hello, fingerprint readers,
    // or Face ID â€” so those users will never see the banner.
    authService.isPlatformAuthenticatorAvailable().then((available) => {
      if (available) setVisible(true);
    });
  }, [user]);

  const handleActivate = async () => {
    if (!user) return;
    setRegistering(true);
    setError(null);
    try {
      await authService.registerPasskey(
        user.uid,
        user.email,
        user.displayName || user.firstName || user.email,
      );
      setDone(true);
      localStorage.setItem(STORAGE_KEY, "true");
      setTimeout(() => setVisible(false), 2500);
    } catch (err: any) {
      // User cancelled the OS prompt â€” dismiss silently
      if (
        err?.name === "NotAllowedError" ||
        err?.message?.toLowerCase().includes("cancel")
      ) {
        localStorage.setItem(STORAGE_KEY, "true");
        setVisible(false);
      } else {
        setError(
          err?.message ||
            t("passkey.errorGeneric", "Could not register passkey. Try again later."),
        );
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 60 }}
          transition={{ type: "spring", damping: 20, delay: 1.5 }}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-md px-4"
          role="complementary"
          aria-label={t("passkey.bannerLabel", "Passkey setup suggestion")}
        >
          <div className="bg-gray-900 border border-teal-500/40 rounded-2xl shadow-2xl p-5 space-y-3">
            {done ? (
              <div className="flex items-center gap-3">
                <CheckCircleIcon className="h-6 w-6 text-teal-400 flex-shrink-0" />
                <p className="text-white font-semibold text-sm">
                  {t(
                    "passkey.successMessage",
                    "Passkey activated! Next time sign in with your fingerprint or Face ID.",
                  )}
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <KeyIcon className="h-7 w-7 text-teal-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white font-semibold text-sm">
                        {t(
                          "passkey.bannerTitle",
                          "Sign in faster with your fingerprint or Face ID",
                        )}
                      </p>
                      <p className="text-white/60 text-xs mt-0.5">
                        {t(
                          "passkey.bannerDescription",
                          "Enable a passkey for instant, passwordless login. This is optional â€” your password still works.",
                        )}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleDismiss}
                    className="text-white/40 hover:text-white/80 flex-shrink-0"
                    aria-label={t("buttons.dismiss", "Dismiss")}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {error && (
                  <p className="text-red-400 text-xs">{error}</p>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={handleActivate}
                    disabled={registering}
                    className="flex-1 py-2 px-4 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60"
                  >
                    {registering
                      ? t("passkey.settingUp", "Setting upâ€¦")
                      : t("passkey.activate", "Activate passkey")}
                  </button>
                  <button
                    onClick={handleDismiss}
                    className="py-2 px-4 bg-white/10 hover:bg-white/20 text-white/80 text-sm rounded-lg transition-colors"
                  >
                    {t("passkey.notNow", "Not now")}
                  </button>
                </div>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PasskeyPromptBanner;
